'use client'

import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { CheckoutButton } from '@tebuto/payment/checkout/CheckoutButton'
import { Banknote, ReceiptText } from 'lucide-react'
import type { ReactNode } from 'react'
import { PaymentMethodChoice } from './PaymentMethodChoice'
import type { WidgetPaymentMethodId, WidgetPaymentOption } from './payment-option.types'
import { StripeCheckoutSessionReloadPrompt } from './StripeCheckoutSessionReloadPrompt'

interface StripePaymentMethodsBodyProps {
    paymentOptions: WidgetPaymentOption[]
    selectedMethodId: WidgetPaymentMethodId | null
    onSelectMethod: (methodId: WidgetPaymentMethodId) => void
    processingMethodId: WidgetPaymentMethodId | null
    isOnlineProcessing: boolean
    hasNativeOnlinePayment: boolean
    onlinePaymentSlot: ReactNode
    redirectReturnErrorMessage: string | null
    onReloadCheckoutSession: () => void
    isReloadingCheckoutSession?: boolean
    selectedOfflineOption: WidgetPaymentOption | null
    onBottomCtaClick: () => void
    bottomCtaDisabled: boolean
    bottomCtaLoading: boolean
    bottomCtaLabel: string
    showBottomCta?: boolean
}

export function StripePaymentMethodsBody({
    paymentOptions,
    selectedMethodId,
    onSelectMethod,
    processingMethodId,
    isOnlineProcessing,
    hasNativeOnlinePayment,
    onlinePaymentSlot,
    redirectReturnErrorMessage,
    onReloadCheckoutSession,
    isReloadingCheckoutSession = false,
    selectedOfflineOption,
    onBottomCtaClick,
    bottomCtaDisabled,
    bottomCtaLoading,
    bottomCtaLabel,
    showBottomCta = true
}: StripePaymentMethodsBodyProps) {
    const { t } = useComponentTranslation()

    return (
        <>
            {redirectReturnErrorMessage ? (
                <StripeCheckoutSessionReloadPrompt
                    message={redirectReturnErrorMessage}
                    onReload={onReloadCheckoutSession}
                    isReloading={isReloadingCheckoutSession}
                    testId="booking-payment-return-error"
                />
            ) : null}

            <div className="flex flex-col gap-3">
                {hasNativeOnlinePayment ? (
                    <div data-testid="booking-payment-native-online-action">{onlinePaymentSlot}</div>
                ) : null}

                {paymentOptions.length > 0 ? (
                    <div
                        className="flex flex-col gap-3"
                        role="radiogroup"
                        aria-label={t('booking.payment.selectMethod')}
                    >
                        {paymentOptions.map((option) => (
                            <PaymentMethodChoice
                                key={option.id}
                                label={option.label}
                                description={option.description}
                                selected={selectedMethodId === option.id}
                                icon={option.id === 'cash' ? <Banknote /> : <ReceiptText />}
                                disabled={isOnlineProcessing || Boolean(processingMethodId)}
                                onSelect={() => onSelectMethod(option.id)}
                                testId={`booking-payment-${option.id}`}
                            />
                        ))}
                    </div>
                ) : null}
            </div>

            {showBottomCta && (selectedOfflineOption || hasNativeOnlinePayment) ? (
                <CheckoutButton
                    type="button"
                    data-testid="booking-payment-submit"
                    onClick={onBottomCtaClick}
                    disabled={bottomCtaDisabled}
                    isLoading={bottomCtaLoading}
                    className="min-h-11 text-base"
                >
                    {bottomCtaLabel}
                </CheckoutButton>
            ) : null}
        </>
    )
}
